/**
 * Credentials API client.
 *
 * Mirrors `backend/src/hub/routes/credentials.py`. Credentials
 * are the typed wrapper around vault secrets — consumers (channels,
 * ingress adapters, workflow http_sink) reference them by id and
 * never see the underlying raw secret bytes.
 */
import { api } from "./api";


export type CredentialType =
  | "smtp"
  | "telegram_bot"
  | "http_bearer"
  | "api_key"
  // OAuth 2.0 client-credentials — exchanges a client id/secret at a token
  // endpoint for a short-lived bearer (used by MCP servers needing OAuth).
  | "oauth2_client_credentials"
  // DB connection types — split from the old generic "db_connection"
  // because every driver has materially different knobs (sslmode for
  // PG, charset/ssl_disabled for MySQL, encrypt/trust_server_certificate
  // for SQL Server). One type per driver lets the picker show only the
  // fields that mean something for that engine.
  | "postgres"
  | "mysql"
  | "sqlserver"
  // Tray Gateway POINTER (ADR-0042) — `{gateway_id, remote_ref}`, NO secret: the
  // DB password lives on the on-prem agent, never in the cloud vault.
  | "tray_gateway"
  | "hmac"
  | "sftp";


export interface Credential {
  id: string;
  name: string;
  description: string | null;
  type: CredentialType;
  config: Record<string, unknown>;
  /** Names of secrets stored. Values NEVER returned by the API. */
  secret_keys: string[];
  enabled: boolean;
  tags: string[];
  last_used_at: string | null;
  last_used_for: string | null;
  created_by: string;
  created_at: string;
  updated_at: string | null;
}


export interface CredentialCreate {
  type: CredentialType;
  name: string;
  description?: string | null;
  config?: Record<string, unknown>;
  secrets?: Record<string, string>;
  enabled?: boolean;
  tags?: string[];
}


export type CredentialUpdate = Partial<
  Omit<CredentialCreate, "type" | "secrets">
>;


export interface CredentialTypeInfo {
  type_id: CredentialType;
  label: string;
  description: string;
  /** UI grouping for the type chooser: database / messaging / http / file / ai / other. */
  category: string;
  /** Pydantic-generated JSON Schema for the non-secret config. */
  config_schema: {
    properties?: Record<string, Record<string, unknown>>;
    required?: string[];
    [k: string]: unknown;
  };
  /** Pydantic-generated JSON Schema for the secret keys (write-only). */
  secrets_schema: {
    properties?: Record<string, Record<string, unknown>>;
    required?: string[];
    [k: string]: unknown;
  };
}


export interface CredentialTestResult {
  ok: boolean;
  message: string;
  details?: Record<string, unknown> | null;
}


export const credentialsApi = {
  list: (params?: { type?: CredentialType; enabled?: boolean; tag?: string[] }) =>
    api.get<Credential[]>("/credentials", { query: params }),

  types: () => api.get<CredentialTypeInfo[]>("/credentials/types"),

  get: (id: string) => api.get<Credential>(`/credentials/${id}`),

  create: (body: CredentialCreate) =>
    api.post<Credential>("/credentials", body),

  update: (id: string, body: CredentialUpdate) =>
    api.patch<Credential>(`/credentials/${id}`, body),

  rotateSecrets: (id: string, secrets: Record<string, string>) =>
    api.post<Credential>(`/credentials/${id}/secrets`, { secrets }),

  remove: (id: string) => api.delete<void>(`/credentials/${id}`),

  test: (id: string) => api.post<CredentialTestResult>(`/credentials/${id}/test`),
};
